/**
 * useInspectionWorkflow - Custom hook managing the 3-step inspection completion workflow.
 *
 * Modeled after useWorkOrderWorkflow but adapted for inspections:
 *   1. Checklist  — inspector rates each item (Good / Needs Attention / Critical)
 *                   with optional per-item photo capture and a required note when
 *                   any item is rated Critical.
 *   2. Verify     — read-only summary of the rated items with the option to add
 *                   general observation photos.
 *   3. Sign-Off   — Recommendations / Remarks + Follow-Up textareas and the
 *                   Submit Inspection button that PATCHes the record:
 *                     - status            -> 'observations_complete'
 *                     - recommendations_remarks
 *                     - follow_up_notes
 *                   No signature: customers sign work orders, never inspections.
 *
 * Differences from the work-order variant:
 *   - No IndexedDB offline support and no multi-tech polling — inspections are
 *     single-inspector by design today, so the simpler in-memory state is fine.
 *   - Per-item completion is a three-tier rating (Good / Needs Attention /
 *     Critical), not a binary toggle. The rating persistence call is made by
 *     the parent step component because the rating-note flow needs UI-level
 *     deferred saves; the hook still owns
 *     the items state and the refetch-after-save coordination.
 */
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
	checklistService,
	ChecklistItem,
} from '../services/checklistService';
import {
	checklistInstanceService,
	ChecklistItemRating,
} from '../services/checklistInstanceService';
import { inspectionService } from '../services/inspectionService';
import type { CapturedPhoto } from '../components/organisms/PhotoCapture/PhotoCapture';
import { qk } from '../lib/queryKeys';
import { ApiError } from '../services/api';
/* Server errors are shown as the server's own sentence ("A note is required
   when rating an item Critical.") rather than the transport's "API request
   failed: Unprocessable Content", which beta testers reported as an
   unexplained "API error". */
import { apiErrorMessage } from '../utils/apiErrorMessage';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** The three sequential steps in the inspection workflow */
export type InspectionWorkflowStep = 'checklist' | 'verify' | 'sign-off';

/**
 * Subset of the inspection record that the workflow needs. Matches the shape
 * returned by inspectionService.getById() after camelization. Kept loose so
 * any future fields read by the workflow can be added without breaking
 * existing callers.
 */
export interface InspectionWorkflowSource {
	id: string | number;
	status: string;
	recommendationsRemarks?: string | null;
	followUpNotes?: string | null;
	companyId?: string | number | null;
}

export interface InspectionWorkflowState {
	/** Checklist items composed via checklistService.getInspectionChecklist (with per-item photos). */
	items: ChecklistItem[];
	/** Whether the initial checklist load is in flight. */
	isLoading: boolean;
	/** Error surfaced by the most recent operation. */
	error: string | null;
	/** Active step. */
	currentStep: InspectionWorkflowStep;
	/** True when every required item has a non-null rating. */
	allItemsRated: boolean;
	/** Photos captured during Step 2 (general observation photos). Uploaded on submitVerification. */
	verificationPhotos: CapturedPhoto[];
	/** True while a step transition or final submit is mid-flight. */
	isSubmitting: boolean;
	/** True once Step 3 submit succeeds — drives the success surface and onComplete callback. */
	isSubmitted: boolean;

	/** Persist a rating change for one item; refetches the checklist on success. */
	rateItem: (
		itemId: string,
		rating: ChecklistItemRating | null,
		ratingNote: string | null,
	) => Promise<void>;
	/**
	 * Rename a checklist item in place. Inspectors routinely find that the
	 * template's wording doesn't match what's actually in front of them
	 * ("Lobby" is really "East Lobby"), so the title is editable during the
	 * visit without having to edit the template.
	 */
	renameItem: (itemId: string, title: string) => Promise<void>;
	/**
	 * Append an ad-hoc item to this inspection's checklist. Only affects this
	 * inspection's instance — the source template is untouched.
	 */
	addItem: (title: string) => Promise<void>;
	/** Navigate to a specific step (with simple gating). */
	goToStep: (step: InspectionWorkflowStep) => void;
	/** Add a verification photo to the local list (uploaded on submitVerification). */
	addVerificationPhoto: (photo: CapturedPhoto) => void;
	/** Remove a verification photo by index. */
	removeVerificationPhoto: (index: number) => void;
	/**
	 * Step-2 commit. Uploads any verificationPhotos as observation photos via
	 * uploadService, then advances to sign-off. Photos are uploaded sequentially
	 * so a partial failure leaves a clear error to retry.
	 */
	submitVerification: (uploadObservationPhoto: (photo: CapturedPhoto) => Promise<void>) => Promise<void>;
	/**
	 * Step-3 commit. PATCHes the inspection with recommendations/follow-up
	 * and transitions status to observations_complete.
	 */
	submitSignOff: (payload: {
		recommendationsRemarks: string;
		followUpNotes: string;
	}) => Promise<void>;
	/** Force-refresh the checklist items from the server. */
	refresh: () => Promise<void>;
}

/* ============================================
 * HOOK
 * ============================================ */

export function useInspectionWorkflow(
	inspectionId: string,
	source: InspectionWorkflowSource | null,
	isOpen: boolean,
	onAfterSubmit?: () => void,
): InspectionWorkflowState {
	const queryClient = useQueryClient();
	const itemsKey = qk.inspectionWorkflow(inspectionId);

	const [currentStep, setCurrentStep] = useState<InspectionWorkflowStep>('checklist');
	const [verificationPhotos, setVerificationPhotos] = useState<CapturedPhoto[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	// Local error for mutation failures; combined with the query error below.
	const [actionError, setActionError] = useState<string | null>(null);

	/* If the inspection is already past observations_complete on mount we
	   show the sign-off step in read-only success mode rather than the
	   blank checklist (matches WO behavior of jumping past completed
	   steps). */
	useEffect(() => {
		if (!source) return;
		const completedStatuses = ['observations_complete', 'corrections_in_progress', 'completed'];
		if (completedStatuses.includes(source.status)) {
			setCurrentStep('sign-off');
			setIsSubmitted(true);
		}
		// Only the status drives this one-time jump-to-sign-off; re-running on
		// every `source` change would fight the user's in-progress navigation.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [source?.status]);

	// --- Checklist items read (TanStack Query) ---
	// Only runs while the overlay is open. Cached + persisted like every other
	// read in the app, and refreshed after offline sync via global invalidation.
	const query = useQuery<ChecklistItem[]>({
		queryKey: itemsKey,
		queryFn: async (): Promise<ChecklistItem[]> => {
			const res = await checklistService.getInspectionChecklist(inspectionId);
			return res.data?.items ?? [];
		},
		enabled: isOpen,
	});

	/* Memoized so the array identity is stable across renders. Without this the
	   `?? []` fallback mints a fresh array every render, which would re-create
	   every useCallback that depends on the item list (addItem reads it to work
	   out the next sort order). */
	const items: ChecklistItem[] = useMemo(() => query.data ?? [], [query.data]);
	const isLoading = isOpen && query.isLoading;
	const error = actionError
		?? (query.error ? 'Failed to load checklist. Please try again.' : null);

	/** Refetch the checklist items from the server. */
	const fetchItems = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey: itemsKey });
	}, [queryClient, itemsKey]);

	/* ---- Derived state ---- */

	/* Required-rating gate: parity with WorkflowChecklist. If the template
	   has no required items, we fall back to "every item rated" so blank
	   templates don't slip through. */
	const requiredItems = items.filter((it) => it.isRequired);
	const allItemsRated = requiredItems.length === 0
		? items.length > 0 && items.every((it) => !!(it as any).rating)
		: requiredItems.every((it) => !!(it as any).rating);

	/* ---- Actions ---- */

	const rateItem = useCallback(
		async (itemId: string, rating: ChecklistItemRating | null, ratingNote: string | null) => {
			/* Optimistic cache update so the rating button reflects the
			   selection immediately, then refetch to pick up server-side
			   attribution (completedByName, completedAt). */
			queryClient.setQueryData<ChecklistItem[]>(itemsKey, (prev: ChecklistItem[] | undefined) =>
				(prev ?? []).map((it: ChecklistItem) =>
					it.id === itemId
						? { ...it, rating, ratingNote }
						: it
				),
			);
			try {
				setActionError(null);
				await checklistInstanceService.rateItem(itemId, rating, ratingNote);
				await fetchItems();
			} catch (err) {
				/* Roll back via refetch — server is the source of truth. */
				console.error('[useInspectionWorkflow] Rate error:', err);
				setActionError(apiErrorMessage(err, 'Could not save that rating. Please try again.'));
				await fetchItems();
			}
		},
		[fetchItems, queryClient, itemsKey],
	);

	/* Rename an item in place. Optimistic so the new title sticks under the
	   inspector's finger immediately; the refetch reconciles with the server. */
	const renameItem = useCallback(
		async (itemId: string, title: string) => {
			const trimmed = title.trim();
			if (!trimmed) return;

			queryClient.setQueryData<ChecklistItem[]>(itemsKey, (prev: ChecklistItem[] | undefined) =>
				(prev ?? []).map((it: ChecklistItem) => (it.id === itemId ? { ...it, title: trimmed } : it)),
			);
			try {
				setActionError(null);
				await checklistInstanceService.updateItem(itemId, { title: trimmed });
				await fetchItems();
			} catch (err) {
				console.error('[useInspectionWorkflow] Rename error:', err);
				setActionError(apiErrorMessage(err, 'Could not rename that item. Please try again.'));
				/* Refetch discards the optimistic title — server is the truth. */
				await fetchItems();
			}
		},
		[fetchItems, queryClient, itemsKey],
	);

	/* Add an ad-hoc item to this inspection only. Not optimistic: the server
	   assigns the id and sort order, and a placeholder row that can't be rated
	   is worse than a brief wait. */
	const addItem = useCallback(
		async (title: string) => {
			const trimmed = title.trim();
			if (!trimmed) return;

			try {
				setActionError(null);
				/* Sort after everything currently on the list so new items land
				   at the bottom in the order they were added. */
				const nextSortOrder = items.reduce((max, it) => Math.max(max, it.sortOrder ?? 0), 0) + 1;
				await checklistInstanceService.addItemToInspection(inspectionId, {
					title: trimmed,
					/* Ad-hoc additions are optional by default — a required item
					   added mid-visit would block the inspector from continuing. */
					isRequired: false,
					sortOrder: nextSortOrder,
				});
				await fetchItems();
			} catch (err) {
				console.error('[useInspectionWorkflow] Add item error:', err);
				setActionError(apiErrorMessage(err, 'Could not add that item. Please try again.'));
			}
		},
		[fetchItems, inspectionId, items],
	);

	const goToStep = useCallback(
		(step: InspectionWorkflowStep) => {
			if (step === 'checklist') {
				setActionError(null);
				setCurrentStep('checklist');
				return;
			}
			if (step === 'verify') {
				if (!allItemsRated) {
					setActionError('Every required item must be rated before verification.');
					return;
				}
				setActionError(null);
				setCurrentStep('verify');
				return;
			}
			if (step === 'sign-off') {
				setActionError(null);
				setCurrentStep('sign-off');
			}
		},
		[allItemsRated],
	);

	const addVerificationPhoto = useCallback((photo: CapturedPhoto) => {
		setVerificationPhotos((prev) => [...prev, photo]);
	}, []);

	const removeVerificationPhoto = useCallback((index: number) => {
		setVerificationPhotos((prev) => prev.filter((_, i) => i !== index));
	}, []);

	/* The actual upload happens in the parent (it owns photoTypeService +
	   uploadService); the hook just orchestrates ordering, error capture,
	   and the step transition. */
	const submitVerification = useCallback(
		async (uploadObservationPhoto: (photo: CapturedPhoto) => Promise<void>) => {
			setIsSubmitting(true);
			setActionError(null);
			try {
				for (const photo of verificationPhotos) {
					await uploadObservationPhoto(photo);
				}
				setVerificationPhotos([]);
				setCurrentStep('sign-off');
			} catch (err) {
				console.error('[useInspectionWorkflow] Verification error:', err);
				setActionError(err instanceof ApiError
					? apiErrorMessage(err, 'Could not upload the photos. Please try again.')
					: (err instanceof Error ? err.message : 'Could not upload the photos. Please try again.'));
			} finally {
				setIsSubmitting(false);
			}
		},
		[verificationPhotos],
	);

	const submitSignOff = useCallback(
		async ({ recommendationsRemarks, followUpNotes }: { recommendationsRemarks: string; followUpNotes: string }) => {
			setIsSubmitting(true);
			setActionError(null);
			try {
				await inspectionService.update(inspectionId, {
					status: 'observations_complete',
					recommendationsRemarks,
					followUpNotes,
				});
				setIsSubmitted(true);
				if (onAfterSubmit) onAfterSubmit();
			} catch (err) {
				console.error('[useInspectionWorkflow] Sign-off error:', err);
				setActionError(apiErrorMessage(err, 'Could not submit the inspection. Please try again.'));
			} finally {
				setIsSubmitting(false);
			}
		},
		[inspectionId, onAfterSubmit],
	);

	const refresh = useCallback(async () => {
		await query.refetch();
	}, [query]);

	return {
		items,
		isLoading,
		error,
		currentStep,
		allItemsRated,
		verificationPhotos,
		isSubmitting,
		isSubmitted,
		rateItem,
		renameItem,
		addItem,
		goToStep,
		addVerificationPhoto,
		removeVerificationPhoto,
		submitVerification,
		submitSignOff,
		refresh,
	};
}
